import {
  Box, Card, CardContent, Typography, Chip, Grid, Table, TableBody,
  TableCell, TableHead, TableRow, Tabs, Tab, CircularProgress, Button,
  Divider, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack, Person, ShoppingBag, AccountBalance, Stars, Phone, Email, LocationOn, Payment,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import api from '../../api/axios';
import { showSnackbar } from '../ui/uiSlice';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-PK') : '—';

function PaymentDialog({ open, customer, onClose, onPaid }) {
  const dispatch = useDispatch();
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const handlePay = async () => {
    if (!amount || +amount <= 0) return;
    setSaving(true);
    try {
      await api.post(`/customers/${customer.id}/payment`, { amount: +amount, method, description: note });
      dispatch(showSnackbar({ message: `Payment of ₨${fmt(+amount)} recorded`, severity: 'success' }));
      onPaid();
      onClose();
      setAmount(''); setNote('');
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Error', severity: 'error' }));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Receive Payment — {customer?.name}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <Typography color="text.secondary">Outstanding: <strong style={{ color: '#ef4444' }}>₨ {fmt(customer?.credit_balance)}</strong></Typography>
        <TextField label="Amount (₨) *" type="number" value={amount} onChange={e => setAmount(e.target.value)}
          inputProps={{ min: 0.01, max: customer?.credit_balance }} fullWidth />
        <TextField label="Payment Method" select value={method} onChange={e => setMethod(e.target.value)} fullWidth>
          {['cash', 'card', 'bank_transfer', 'cheque'].map(m => <option key={m} value={m}>{m}</option>)}
        </TextField>
        <TextField label="Note" value={note} onChange={e => setNote(e.target.value)} fullWidth />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handlePay} disabled={saving || !amount}>
          {saving ? 'Saving…' : 'Record Payment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [payOpen, setPayOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/customers/${id}`);
      setData(res.data);
    } catch {
      dispatch(showSnackbar({ message: 'Customer not found', severity: 'error' }));
      navigate('/customers');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!data) return null;

  const sales = data.sales || [];
  const ledger = data.ledger || [];
  const totalSpent = sales.reduce((s, sale) => s + +sale.total_amount, 0);
  const avgOrder = sales.length ? totalSpent / sales.length : 0;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/customers')} variant="text">Back</Button>
        <Typography variant="h5" fontWeight={700}>{data.name}</Typography>
        <Chip label={data.credit_balance > 0 ? 'Has Balance Due' : 'Clear'} color={data.credit_balance > 0 ? 'error' : 'success'} size="small" />
        {data.credit_balance > 0 && (
          <Button variant="contained" color="success" startIcon={<Payment />} size="small" onClick={() => setPayOpen(true)}>
            Receive Payment
          </Button>
        )}
      </Box>

      {/* Profile + KPI cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: 22 }}>
                  {data.name?.[0]?.toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight={700}>{data.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Customer since {fmtDate(data.created_at)}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {data.phone && <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <Phone fontSize="small" color="action" />
                <Typography variant="body2">{data.phone}</Typography>
              </Box>}
              {data.email && <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                <Email fontSize="small" color="action" />
                <Typography variant="body2">{data.email}</Typography>
              </Box>}
              {(data.city || data.address) && <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                <LocationOn fontSize="small" color="action" sx={{ mt: 0.2 }} />
                <Typography variant="body2">{[data.address, data.city].filter(Boolean).join(', ')}</Typography>
              </Box>}
              {data.credit_limit > 0 && (
                <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary">Credit Limit</Typography>
                  <Typography fontWeight={700}>₨ {fmt(data.credit_limit)}</Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {[
              { label: 'Total Spent', value: `₨ ${fmt(totalSpent)}`, color: 'primary.main', icon: <ShoppingBag /> },
              { label: 'Total Orders', value: sales.length, color: 'info.main', icon: <ShoppingBag /> },
              { label: 'Avg Order Value', value: `₨ ${fmt(avgOrder)}`, color: 'success.main', icon: <AccountBalance /> },
              { label: 'Loyalty Points', value: `${data.loyalty_points || 0} pts`, color: 'warning.main', icon: <Stars /> },
              { label: 'Amount Due', value: `₨ ${fmt(data.credit_balance)}`, color: data.credit_balance > 0 ? 'error.main' : 'success.main', icon: <AccountBalance /> },
            ].map((kpi, i) => (
              <Grid item xs={6} sm={4} key={i}>
                <Card>
                  <CardContent sx={{ pb: '16px !important' }}>
                    <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                    <Typography variant="h6" fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label={`Purchase History (${sales.length})`} icon={<ShoppingBag />} iconPosition="start" />
          <Tab label={`Credit Ledger (${ledger.length})`} icon={<AccountBalance />} iconPosition="start" />
        </Tabs>
        <CardContent sx={{ p: 0 }}>
          {tab === 0 && (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Invoice #</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Payment Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sales.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>No purchases yet</TableCell></TableRow>
                )}
                {sales.map(s => (
                  <TableRow key={s.id} hover>
                    <TableCell><Typography fontFamily="monospace" fontWeight={600}>{s.invoice_number}</Typography></TableCell>
                    <TableCell>{fmtDate(s.created_at)}</TableCell>
                    <TableCell align="right"><Typography fontWeight={700}>₨ {fmt(s.total_amount)}</Typography></TableCell>
                    <TableCell>
                      <Chip
                        label={s.payment_status}
                        size="small"
                        color={s.payment_status === 'paid' ? 'success' : s.payment_status === 'partial' ? 'warning' : 'error'}
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {tab === 1 && (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right">Debit</TableCell>
                  <TableCell align="right">Credit</TableCell>
                  <TableCell align="right">Balance After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ledger.length === 0 && (
                  <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>No ledger entries</TableCell></TableRow>
                )}
                {ledger.map(tx => (
                  <TableRow key={tx.id} hover>
                    <TableCell>{fmtDate(tx.entry_date || tx.created_at)}</TableCell>
                    <TableCell>{tx.description}</TableCell>
                    <TableCell align="right">{tx.type === 'debit' ? <Typography color="error.main">₨ {fmt(tx.amount)}</Typography> : '—'}</TableCell>
                    <TableCell align="right">{tx.type === 'credit' ? <Typography color="success.main">₨ {fmt(tx.amount)}</Typography> : '—'}</TableCell>
                    <TableCell align="right"><Typography fontWeight={600}>₨ {fmt(tx.balance_after)}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <PaymentDialog open={payOpen} customer={data} onClose={() => setPayOpen(false)} onPaid={load} />
    </Box>
  );
}
